import React from 'react';
import { Coins } from 'lucide-react';
import { SessionTokenUsage } from '@/types';
import { useTranslation, formatCount, pluralize } from '@/i18n';
import { clsx } from 'clsx';
import { useAppStore } from '@/stores/appStore';

interface SessionTokenBadgeProps {
  /** Local running total — shown only until the server snapshot arrives. */
  tokenUsage?: SessionTokenUsage;
  sessionId?: string;
  className?: string;
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toLocaleString();
};

const formatCostRub = (cost: number): string => {
  if (cost === 0) return '';
  if (cost < 0.01) {
    return `${cost.toFixed(4)} ₽`;
  }
  return `${cost.toFixed(2)} ₽`;
};

/**
 * Session token/cost badge.
 *
 * STORY-290 (R-4): there are two of these (chat header, input footer). Each
 * used to hold its own data — the header polled the cost endpoint on its own
 * timer, the footer summed the local token_usage of each answer (which misses
 * e.g. the intent-dispatch classifier call) — so mid-session they showed
 * different numbers (3.6K vs 4.0K). Both now read the same store snapshot,
 * refreshed once after every answer (useChatService → refreshSessionStats),
 * and fall back to the same local total only before the first snapshot.
 */
export const SessionTokenBadge: React.FC<SessionTokenBadgeProps> = ({
  tokenUsage,
  sessionId,
  className
}) => {
  const { t: tr, language } = useTranslation();
  const serverData = useAppStore((state) => (sessionId ? state.sessionStats[sessionId] : undefined));

  const totalTokens = serverData?.total_tokens ?? tokenUsage?.totalTokens ?? 0;
  const costRub = serverData ? serverData.total_cost_rub : (tokenUsage?.estimatedCostUsd ?? 0);
  const requestCount = serverData?.request_count ?? tokenUsage?.requestCount ?? 0;
  const modelsUsed = serverData?.models_used ?? [];

  // Don't render if no usage
  if (totalTokens === 0) {
    return null;
  }

  const costDisplay = costRub > 0 ? formatCostRub(costRub) : '';
  const tokensLabel = pluralize(language, totalTokens, tr.analytics.counts.tokens);

  const tooltipText = language === 'ru'
    ? `Сессия: ${formatCount(language, totalTokens, tr.analytics.counts.tokens, totalTokens.toLocaleString())} (${formatCount(language, requestCount, tr.stats.requestsCount)})${costDisplay ? ` • ${costDisplay}` : ''}${modelsUsed.length > 0 ? `\nМодели: ${modelsUsed.join(', ')}` : ''}`
    : `Session: ${formatCount(language, totalTokens, tr.analytics.counts.tokens, totalTokens.toLocaleString())} (${formatCount(language, requestCount, tr.stats.requestsCount)})${costDisplay ? ` • ${costDisplay}` : ''}${modelsUsed.length > 0 ? `\nModels: ${modelsUsed.join(', ')}` : ''}`;

  return (
    <div
      className={clsx(
        'inline-flex items-center gap-1.5 px-2 py-1 rounded-md',
        'text-xs text-gray-400 dark:text-gray-500',
        'bg-gray-100/50 dark:bg-gray-800/50',
        'border border-gray-200/50 dark:border-gray-700/50',
        'transition-colors hover:text-gray-500 dark:hover:text-gray-400',
        'select-none cursor-default',
        className
      )}
      title={tooltipText}
    >
      <Coins className="h-3 w-3 opacity-60" />
      <span className="font-medium tabular-nums">
        {formatNumber(totalTokens)}
      </span>
      <span className="opacity-60 hidden sm:inline">
        {tokensLabel}
      </span>
      {costDisplay && (
        <>
          <span className="opacity-40 hidden sm:inline">·</span>
          <span className="opacity-70 hidden sm:inline tabular-nums">{costDisplay}</span>
        </>
      )}
    </div>
  );
};
